import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Users,
  Plus,
  Search,
  RefreshCw,
  MoreVertical,
  Trash2,
  Edit2,
  Eye,
  Target,
  Globe,
  UserPlus,
  TrendingUp,
  Filter,
  Zap,
  UploadCloud,
  Upload,
  ExternalLink,
  Link,
  Copy,
  Code,
  Check
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import audienceService, { Audience, LandingPageOption, ExternalWebsite } from '../../services/audienceService';

const AudiencesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAudience, setSelectedAudience] = useState<Audience | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // CSV Import state (for customer_list type)
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<Array<{ email?: string; phone?: string; first_name?: string; last_name?: string }>>([]);

  // External website modal state
  const [isExternalModalOpen, setIsExternalModalOpen] = useState(false);
  const [externalWebsites, setExternalWebsites] = useState<ExternalWebsite[]>([]);
  const [externalForm, setExternalForm] = useState({ name: '', url: '' });
  const [isAddingExternal, setIsAddingExternal] = useState(false);
  const [copiedPixelId, setCopiedPixelId] = useState<string | null>(null);

  // Create form state
  const [landingPages, setLandingPages] = useState<LandingPageOption[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'website_visitors' as Audience['type'],
    source: 'landing_page' as 'landing_page' | 'pixel_events',
    landing_page_ids: [] as string[],
    lookback_days: 30,
    refresh_frequency: 'daily' as Audience['refresh_frequency'],
    is_dynamic: true,
    pixel_event_types: [] as string[]
  });
  const [estimatedSize, setEstimatedSize] = useState<number | null>(null);

  useEffect(() => {
    loadAudiences();
    loadLandingPages();
  }, []);

  const loadAudiences = async () => {
    setIsLoading(true);
    try {
      const response = await audienceService.getAudiences({
        search: searchQuery || undefined,
        type: filterType || undefined,
        status: filterStatus || undefined
      });
      if (response.success) {
        setAudiences(response.data);
      }
    } catch (error) {
      toast.error(t('audiences.errorLoading', 'Error loading audiences'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadLandingPages = async () => {
    try {
      const response = await audienceService.getAvailableLandingPages();
      if (response.success) {
        setLandingPages(response.data);
      }
    } catch (error) {
      console.error('Error loading landing pages:', error);
    }
  };

  const loadExternalWebsites = async () => {
    try {
      const response = await audienceService.getExternalWebsites();
      if (response.success) {
        setExternalWebsites(response.data);
      }
    } catch (error) {
      console.error('Error loading external websites:', error);
    }
  };

  const handleAddExternalWebsite = async () => {
    if (!externalForm.name.trim() || !externalForm.url.trim()) {
      toast.error(t('audiences.externalNameUrlRequired', 'Name and URL are required'));
      return;
    }

    setIsAddingExternal(true);
    try {
      const response = await audienceService.createExternalWebsite({
        name: externalForm.name,
        url: externalForm.url
      });
      if (response.success) {
        toast.success(t('audiences.externalCreated', 'External site registered successfully!'));
        setExternalForm({ name: '', url: '' });
        loadExternalWebsites();
        loadLandingPages(); // Refresh landing pages list to include new external site
      }
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || t('audiences.errorCreatingExternal', 'Erro ao cadastrar site externo');
      toast.error(msg);
    } finally {
      setIsAddingExternal(false);
    }
  };

  const handleDeleteExternalWebsite = async (id: string) => {
    try {
      const response = await audienceService.deleteExternalWebsite(id);
      if (response.success) {
        toast.success(t('audiences.externalDeleted', 'External site removed'));
        loadExternalWebsites();
        loadLandingPages();
      }
    } catch (error: any) {
      toast.error(error.message || t('audiences.errorDeletingExternal', 'Error removing external site'));
    }
  };

  const handleCopyPixel = (snippet: string, id: string) => {
    navigator.clipboard.writeText(snippet);
    setCopiedPixelId(id);
    toast.success(t('audiences.pixelCopied', 'Pixel copied!'));
    setTimeout(() => setCopiedPixelId(null), 2000);
  };

  const handleSearch = () => {
    loadAudiences();
  };

  const handleCreateAudience = async () => {
    if (!formData.name.trim()) {
      toast.error(t('audiences.nameRequired', 'Name is required'));
      return;
    }

    if (['website_visitors', 'leads', 'converters'].includes(formData.type) && formData.landing_page_ids.length === 0) {
      toast.error(t('audiences.selectLandingPage', 'Select at least one landing page or site'));
      return;
    }

    if (formData.type === 'customer_list' && !csvFile) {
      toast.error(t('audiences.selectCSV', 'Select a CSV file'));
      return;
    }

    if (formData.source === 'pixel_events' && formData.pixel_event_types.length === 0) {
      toast.error(t('audiences.selectPixelEvents', 'Selecione pelo menos um tipo de evento do pixel'));
      return;
    }

    setIsSubmitting(true);
    try {
      // Create the audience first
      const resolvedSource = formData.type === 'customer_list'
        ? 'csv_upload'
        : formData.source === 'pixel_events'
          ? 'pixel_events'
          : 'landing_page';
      const response = await audienceService.createAudience({
        ...formData,
        source: resolvedSource,
        is_dynamic: formData.is_dynamic,
        pixel_event_types: formData.source === 'pixel_events' ? formData.pixel_event_types : undefined
      });

      if (response.success) {
        const audienceId = response.data.id;

        // If customer_list, upload the CSV data
        if (formData.type === 'customer_list' && csvFile) {
          const text = await csvFile.text();
          const lines = text.split('\n').filter(line => line.trim());
          const firstLine = lines[0];
          const delimiter = firstLine.includes(';') ? ';' : ',';
          const headers = firstLine.split(delimiter).map(h => h.trim().toLowerCase().replace(/["']/g, ''));

          const headerMap: Record<string, string> = {
            'email': 'email', 'e-mail': 'email', 'e_mail': 'email',
            'phone': 'phone', 'telefone': 'phone', 'tel': 'phone', 'celular': 'phone', 'mobile': 'phone',
            'first_name': 'first_name', 'firstname': 'first_name', 'primeiro_nome': 'first_name', 'nome': 'first_name',
            'last_name': 'last_name', 'lastname': 'last_name', 'sobrenome': 'last_name', 'ultimo_nome': 'last_name',
            'country': 'country', 'pais': 'country'
          };

          const columnIndices: Record<string, number> = {};
          headers.forEach((h, idx) => {
            const mapped = headerMap[h];
            if (mapped) columnIndices[mapped] = idx;
          });

          const users: Array<{ email?: string; phone?: string; first_name?: string; last_name?: string; country?: string }> = [];
          const dataLines = lines.slice(1);

          for (const line of dataLines) {
            const values = line.split(delimiter).map(v => v.trim().replace(/["']/g, ''));
            const row: any = {};

            if (columnIndices.email !== undefined && values[columnIndices.email]) row.email = values[columnIndices.email];
            if (columnIndices.phone !== undefined && values[columnIndices.phone]) row.phone = values[columnIndices.phone];
            if (columnIndices.first_name !== undefined) row.first_name = values[columnIndices.first_name];
            if (columnIndices.last_name !== undefined) row.last_name = values[columnIndices.last_name];
            if (columnIndices.country !== undefined) row.country = values[columnIndices.country];

            if (row.email || row.phone) {
              users.push(row);
            }
          }

          if (users.length > 0) {
            const uploadResponse = await audienceService.addUsersToAudience(audienceId, users);
            if (uploadResponse.success) {
              toast.success(t('audiences.createdWithUsers', 'Audience created with {{count}} users')
                .replace('{{count}}', uploadResponse.data.added.toString()));
            }
          }
        } else {
          toast.success(t('audiences.created', 'Audience created successfully'));
        }

        setIsCreateModalOpen(false);
        resetForm();
        setCsvFile(null);
        setCsvPreview([]);
        loadAudiences();
      }
    } catch (error: any) {
      toast.error(error.message || t('audiences.errorCreating', 'Error creating audience'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAudience = async () => {
    if (!selectedAudience) return;

    setIsSubmitting(true);
    try {
      const response = await audienceService.deleteAudience(selectedAudience.id);
      if (response.success) {
        toast.success(t('audiences.deleted', 'Audience deleted'));
        setIsDeleteModalOpen(false);
        setSelectedAudience(null);
        loadAudiences();
      }
    } catch (error: any) {
      toast.error(error.message || t('audiences.errorDeleting', 'Error deleting audience'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefreshAudience = async (audience: Audience) => {
    try {
      toast.loading(t('audiences.refreshing', 'Refreshing audience...'), { id: 'refresh' });
      const response = await audienceService.refreshAudience(audience.id);
      if (response.success) {
        toast.success(t('audiences.refreshed', 'Audience updated! {{size}} users').replace('{{size}}', response.data.size), { id: 'refresh' });
        loadAudiences();
      }
    } catch (error: any) {
      toast.error(error.message || t('audiences.errorRefreshing', 'Error updating'), { id: 'refresh' });
    }
  };

  const handleSyncAudience = async (audience: Audience, platform: 'google' | 'meta' | 'linkedin' | 'all') => {
    try {
      toast.loading(t('audiences.syncing', 'Syncing...'), { id: 'sync' });
      const response = await audienceService.syncAudienceToPlatform(audience.id, platform);
      if (response.success) {
        toast.success(t('audiences.synced', 'Synced successfully!'), { id: 'sync' });
        loadAudiences();
      }
    } catch (error: any) {
      toast.error(error.message || t('audiences.errorSyncing', 'Error syncing'), { id: 'sync' });
    }
  };

  const handlePreviewSize = async () => {
    if (['website_visitors', 'leads', 'converters'].includes(formData.type) && formData.landing_page_ids.length === 0) {
      setEstimatedSize(null);
      return;
    }

    try {
      const response = await audienceService.previewAudienceSize({
        type: formData.type,
        landing_page_ids: formData.landing_page_ids,
        lookback_days: formData.lookback_days
      });
      if (response.success) {
        setEstimatedSize(response.data.estimated_size);
      }
    } catch (error) {
      console.error('Error previewing size:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'website_visitors',
      source: 'landing_page',
      landing_page_ids: [],
      lookback_days: 30,
      refresh_frequency: 'daily',
      is_dynamic: true,
      pixel_event_types: []
    });
    setEstimatedSize(null);
  };

  // CSV Import functions
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      toast.error(t('audiences.invalidFileType', 'File must be CSV or TXT'));
      return;
    }

    setCsvFile(file);
    parseCSVPreview(file);
  };

  const parseCSVPreview = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length === 0) {
        toast.error(t('audiences.emptyFile', 'Empty file'));
        return;
      }

      // Detect delimiter
      const firstLine = lines[0];
      const delimiter = firstLine.includes(';') ? ';' : ',';

      // Parse header
      const headers = firstLine.split(delimiter).map(h => h.trim().toLowerCase().replace(/["']/g, ''));

      // Map common header variations
      const headerMap: Record<string, string> = {
        'email': 'email',
        'e-mail': 'email',
        'e_mail': 'email',
        'phone': 'phone',
        'telefone': 'phone',
        'tel': 'phone',
        'celular': 'phone',
        'mobile': 'phone',
        'first_name': 'first_name',
        'firstname': 'first_name',
        'primeiro_nome': 'first_name',
        'nome': 'first_name',
        'last_name': 'last_name',
        'lastname': 'last_name',
        'sobrenome': 'last_name',
        'ultimo_nome': 'last_name',
        'country': 'country',
        'pais': 'country'
      };

      const columnIndices: Record<string, number> = {};
      headers.forEach((h, idx) => {
        const mapped = headerMap[h];
        if (mapped) {
          columnIndices[mapped] = idx;
        }
      });

      // Parse data rows (preview first 5)
      const previewData: Array<{ email?: string; phone?: string; first_name?: string; last_name?: string }> = [];
      const dataLines = lines.slice(1, 6); // First 5 data rows

      for (const line of dataLines) {
        const values = line.split(delimiter).map(v => v.trim().replace(/["']/g, ''));
        const row: any = {};

        if (columnIndices.email !== undefined) row.email = values[columnIndices.email];
        if (columnIndices.phone !== undefined) row.phone = values[columnIndices.phone];
        if (columnIndices.first_name !== undefined) row.first_name = values[columnIndices.first_name];
        if (columnIndices.last_name !== undefined) row.last_name = values[columnIndices.last_name];
        if (columnIndices.country !== undefined) row.country = values[columnIndices.country];

        if (row.email || row.phone) {
          previewData.push(row);
        }
      }

      setCsvPreview(previewData);

      if (previewData.length === 0) {
        toast.error(t('audiences.noValidData', 'No valid data found. Make sure you have email or phone columns.'));
      }
    };
    reader.readAsText(file);
  };

  const getTypeLabel = (type: string, source?: string) => {
    if (type === 'website_visitors' && source === 'pixel_events') {
      return t('audiences.types.pixelTracking', 'Audiência por Pixel');
    }
    const labels: Record<string, string> = {
      website_visitors: t('audiences.types.websiteVisitors', 'Visitantes do Site'),
      customer_list: t('audiences.types.customerList', 'Lista de Clientes'),
      leads: t('audiences.types.leads', 'Leads'),
      converters: t('audiences.types.converters', 'Convertidos'),
      cart_abandoners: t('audiences.types.cartAbandoners', 'Abandono'),
      lookalike: t('audiences.types.lookalike', 'Semelhante'),
      custom_rule: t('audiences.types.customRule', 'Personalizada')
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, React.ReactNode> = {
      website_visitors: <Globe className="w-4 h-4" />,
      customer_list: <Upload className="w-4 h-4" />,
      leads: <UserPlus className="w-4 h-4" />,
      converters: <Target className="w-4 h-4" />,
      lookalike: <Users className="w-4 h-4" />
    };
    return icons[type] || <Users className="w-4 h-4" />;
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'danger' | 'gray' | 'primary'> = {
      ready: 'success',
      building: 'warning',
      syncing: 'primary',
      error: 'danger',
      archived: 'gray'
    };
    const labels: Record<string, string> = {
      ready: t('audiences.status.ready', 'Pronta'),
      building: t('audiences.status.building', 'Construindo'),
      syncing: t('audiences.status.syncing', 'Sincronizando'),
      error: t('audiences.status.error', 'Erro'),
      archived: t('audiences.status.archived', 'Arquivada')
    };
    return <Badge variant={variants[status] || 'gray'}>{labels[status] || status}</Badge>;
  };

  const toggleLandingPage = (lpId: string) => {
    setFormData(prev => ({
      ...prev,
      landing_page_ids: prev.landing_page_ids.includes(lpId)
        ? prev.landing_page_ids.filter(id => id !== lpId)
        : [...prev.landing_page_ids, lpId]
    }));
  };

  useEffect(() => {
    if (isCreateModalOpen) {
      handlePreviewSize();
    }
  }, [formData.type, formData.landing_page_ids, formData.lookback_days]);

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Target className="w-7 h-7 text-primary-600" />
            {t('audiences.title', 'Audiencias')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('audiences.subtitle', 'Crie e gerencie audiencias para remarketing')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              loadExternalWebsites();
              setIsExternalModalOpen(true);
            }}
            leftIcon={<ExternalLink className="w-4 h-4" />}
          >
            {t('audiences.externalWebsites', 'Sites Externos')}
          </Button>
          <Button onClick={() => setIsCreateModalOpen(true)} leftIcon={<Plus className="w-4 h-4" />}>
            {t('audiences.create', 'Nova Audiencia')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder={t('audiences.searchPlaceholder', 'Buscar audiencias...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                leftIcon={<Search className="w-4 h-4" />}
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">{t('audiences.allTypes', 'Todos os Tipos')}</option>
              <option value="website_visitors">{t('audiences.types.websiteVisitors', 'Visitantes do Site')}</option>
              <option value="customer_list">{t('audiences.types.customerList', 'Lista de Clientes')}</option>
              <option value="leads">{t('audiences.types.leads', 'Leads')}</option>
              <option value="converters">{t('audiences.types.converters', 'Convertidos')}</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">{t('audiences.allStatus', 'Todos os Status')}</option>
              <option value="ready">{t('audiences.status.ready', 'Pronta')}</option>
              <option value="building">{t('audiences.status.building', 'Construindo')}</option>
              <option value="syncing">{t('audiences.status.syncing', 'Sincronizando')}</option>
            </select>
            <Button variant="secondary" onClick={handleSearch} leftIcon={<Filter className="w-4 h-4" />}>
              {t('common.filter', 'Filtrar')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audiences Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 skeleton rounded-xl" />
          ))}
        </div>
      ) : audiences.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Target className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t('audiences.noAudiences', 'Nenhuma audiencia criada')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {t('audiences.noAudiencesDesc', 'Crie sua primeira audiencia para comecar a fazer remarketing')}
            </p>
            <Button onClick={() => setIsCreateModalOpen(true)} leftIcon={<Plus className="w-4 h-4" />}>
              {t('audiences.create', 'Nova Audiencia')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {audiences.map((audience) => (
            <Card key={audience.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      audience.type === 'website_visitors' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' :
                      audience.type === 'leads' ? 'bg-green-100 dark:bg-green-900/30 text-green-600' :
                      audience.type === 'converters' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600' :
                      'bg-gray-100 dark:bg-gray-800 text-gray-600'
                    }`}>
                      {getTypeIcon(audience.type)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">{audience.name}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{getTypeLabel(audience.type, audience.source)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {audience.is_dynamic === false && (
                      <Badge variant="gray">{t('audiences.static', 'Estática')}</Badge>
                    )}
                    {getStatusBadge(audience.status)}
                  </div>
                </div>

                {audience.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                    {audience.description}
                  </p>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {audience.size.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('audiences.users', 'Usuarios')}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {audience.matched_size.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('audiences.matched', 'Matched')}
                    </p>
                  </div>
                </div>

                {/* Platform sync status */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('audiences.syncedTo', 'Sincronizado:')}
                  </span>
                  <div className="flex gap-1">
                    {audience.external_id_google && (
                      <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 text-xs rounded">
                        Google
                      </span>
                    )}
                    {audience.external_id_meta && (
                      <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 text-xs rounded">
                        Meta
                      </span>
                    )}
                    {audience.external_id_linkedin && (
                      <span className="px-2 py-0.5 bg-sky-100 dark:bg-sky-900/30 text-sky-600 text-xs rounded">
                        LinkedIn
                      </span>
                    )}
                    {!audience.external_id_google && !audience.external_id_meta && !audience.external_id_linkedin && (
                      <span className="text-xs text-gray-400">{t('audiences.notSynced', 'Nenhum')}</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/audiences/${audience.id}`)}
                    leftIcon={<Eye className="w-4 h-4" />}
                  >
                    {t('common.view', 'Ver')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRefreshAudience(audience)}
                    leftIcon={<RefreshCw className="w-4 h-4" />}
                  >
                    {t('common.refresh', 'Atualizar')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSyncAudience(audience, 'all')}
                    leftIcon={<UploadCloud className="w-4 h-4" />}
                  >
                    {t('audiences.sync', 'Sync')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 ml-auto"
                    onClick={() => {
                      setSelectedAudience(audience);
                      setIsDeleteModalOpen(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Audience Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          resetForm();
        }}
        title={t('audiences.createTitle', 'Nova Audiencia')}
        size="lg"
      >
        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('audiences.name', 'Nome')} *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('audiences.namePlaceholder', 'Ex: Visitantes Black Friday')}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('audiences.description', 'Descricao')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('audiences.descriptionPlaceholder', 'Descreva o proposito desta audiencia...')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              rows={2}
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('audiences.type', 'Tipo')} *
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { value: 'website_visitors', source: 'pixel_events', label: t('audiences.types.pixelTracking', 'Audiência por Pixel'), icon: <Target className="w-5 h-5" />, desc: t('audiences.typesDesc.pixelTracking', 'Segmente pelo fluxo do site via pixel tracking') },
                { value: 'website_visitors', source: 'landing_page', label: t('audiences.types.websiteVisitors', 'Visitantes do Site'), icon: <Globe className="w-5 h-5" />, desc: t('audiences.typesDesc.websiteVisitors', 'Pessoas que visitaram suas landing pages') },
                { value: 'leads', source: 'landing_page', label: t('audiences.types.leads', 'Leads'), icon: <UserPlus className="w-5 h-5" />, desc: t('audiences.typesDesc.leads', 'Leads capturados em formularios') },
                { value: 'converters', source: 'landing_page', label: t('audiences.types.converters', 'Convertidos'), icon: <Target className="w-5 h-5" />, desc: t('audiences.typesDesc.converters', 'Pessoas que ja converteram') },
                { value: 'customer_list', source: 'landing_page', label: t('audiences.types.customerList', 'Lista de Clientes'), icon: <Upload className="w-5 h-5" />, desc: t('audiences.typesDesc.customerList', 'Upload de lista de emails/telefones') }
              ].map((type) => {
                const isSelected = formData.type === type.value && formData.source === type.source;
                return (
                <button
                  key={`${type.value}-${type.source}`}
                  type="button"
                  onClick={() => setFormData({
                    ...formData,
                    type: type.value as Audience['type'],
                    source: type.source as 'landing_page' | 'pixel_events',
                    landing_page_ids: [],
                    pixel_event_types: type.source !== 'pixel_events' ? [] : formData.pixel_event_types
                  })}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    isSelected
                      ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={isSelected ? 'text-primary-600' : 'text-gray-500'}>
                      {type.icon}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">{type.label}</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{type.desc}</p>
                </button>
                );
              })}
            </div>
          </div>

          {/* Pixel Event Types - shown when source is pixel_events */}
          {formData.source === 'pixel_events' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('audiences.pixelEventTypes', 'Eventos do Pixel')} *
              </label>
              <p className="text-xs text-gray-500 mb-2">
                {t('audiences.pixelEventTypesDesc', 'Selecione quais ações do site definem esta audiência')}
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { value: 'page_view', label: t('audiences.events.pageView', 'Visualização de Página') },
                  { value: 'scroll_depth', label: t('audiences.events.scrollDepth', 'Profundidade de Scroll') },
                  { value: 'time_on_page', label: t('audiences.events.timeOnPage', 'Tempo na Página') },
                  { value: 'click', label: t('audiences.events.click', 'Cliques') },
                  { value: 'form_start', label: t('audiences.events.formStart', 'Início de Formulário') },
                  { value: 'form_submit', label: t('audiences.events.formSubmit', 'Envio de Formulário') },
                  { value: 'button_click', label: t('audiences.events.buttonClick', 'Clique em Botão') },
                  { value: 'link_click', label: t('audiences.events.linkClick', 'Clique em Link') },
                  { value: 'video_play', label: t('audiences.events.videoPlay', 'Play em Vídeo') },
                  { value: 'video_complete', label: t('audiences.events.videoComplete', 'Vídeo Completo') },
                  { value: 'conversion', label: t('audiences.events.conversion', 'Conversão') },
                  { value: 'add_to_cart', label: t('audiences.events.addToCart', 'Adição ao Carrinho') },
                ].map((event) => (
                  <label
                    key={event.value}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                      formData.pixel_event_types.includes(event.value)
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.pixel_event_types.includes(event.value)}
                      onChange={() => {
                        setFormData(prev => ({
                          ...prev,
                          pixel_event_types: prev.pixel_event_types.includes(event.value)
                            ? prev.pixel_event_types.filter(e => e !== event.value)
                            : [...prev.pixel_event_types, event.value]
                        }));
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{event.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Landing Pages Selection (includes external websites for website_visitors only) */}
          {(formData.type === 'website_visitors' || formData.type === 'leads' || formData.type === 'converters') && (() => {
            // External sites only generate pixel events, not leads/conversions via forms
            // So filter them out for leads/converters types
            const showExternalSites = formData.type === 'website_visitors';
            const filteredPages = showExternalSites
              ? landingPages
              : landingPages.filter(lp => !lp.is_external);

            return (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {showExternalSites
                  ? t('audiences.selectSources', 'Selecione as Landing Pages / Sites')
                  : t('audiences.selectLandingPages', 'Selecione as Landing Pages')} *
              </label>
              {filteredPages.length === 0 ? (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center space-y-2">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {showExternalSites
                      ? t('audiences.noSources', 'Nenhuma landing page publicada ou site externo cadastrado.')
                      : t('audiences.noLandingPages', 'Nenhuma landing page publicada. Publique uma landing page primeiro.')}
                  </p>
                  {showExternalSites && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        loadExternalWebsites();
                        setIsExternalModalOpen(true);
                      }}
                      leftIcon={<ExternalLink className="w-4 h-4" />}
                    >
                      {t('audiences.addExternalSite', 'Cadastrar Site Externo')}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredPages.map((lp) => (
                    <label
                      key={lp.id}
                      className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                        formData.landing_page_ids.includes(lp.id) ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.landing_page_ids.includes(lp.id)}
                        onChange={() => toggleLandingPage(lp.id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900 dark:text-white truncate">{lp.name}</p>
                          {lp.is_external && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 text-[10px] font-medium rounded">
                              <ExternalLink className="w-3 h-3" />
                              Externo
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{lp.published_url}</p>
                      </div>
                      <div className="text-right text-xs">
                        <p className="text-gray-600 dark:text-gray-400">{lp.views} {t('audiences.views', 'visitas')}</p>
                        <p className="text-green-600">{lp.conversions} {t('audiences.conversions', 'conversoes')}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            );
          })()}

          {/* CSV Upload for Customer List */}
          {formData.type === 'customer_list' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('audiences.uploadCSV', 'Upload de Planilha (CSV)')} *
              </label>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileChange}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                  {csvFile ? (
                    <p className="text-sm font-medium text-primary-600">{csvFile.name}</p>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('audiences.clickToUpload', 'Clique para selecionar arquivo')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">CSV ou TXT</p>
                    </>
                  )}
                </label>
              </div>

              {/* CSV Format Help */}
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs">
                <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('audiences.csvFormat', 'Formato esperado:')}
                </p>
                <p className="text-gray-500 dark:text-gray-400">
                  {t('audiences.csvColumns', 'Colunas: email, phone, first_name, last_name, country')}
                </p>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                  {t('audiences.csvExample', 'Ex: email;telefone;nome;sobrenome')}
                </p>
              </div>

              {/* CSV Preview */}
              {csvPreview.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('audiences.preview', 'Preview')} ({csvPreview.length} {t('audiences.rows', 'linhas')}):
                  </p>
                  <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          {csvPreview[0]?.email !== undefined && <th className="px-3 py-2 text-left">Email</th>}
                          {csvPreview[0]?.phone !== undefined && <th className="px-3 py-2 text-left">Telefone</th>}
                          {csvPreview[0]?.first_name !== undefined && <th className="px-3 py-2 text-left">Nome</th>}
                          {csvPreview[0]?.last_name !== undefined && <th className="px-3 py-2 text-left">Sobrenome</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {csvPreview.map((row, idx) => (
                          <tr key={idx}>
                            {row.email !== undefined && <td className="px-3 py-2">{row.email}</td>}
                            {row.phone !== undefined && <td className="px-3 py-2">{row.phone}</td>}
                            {row.first_name !== undefined && <td className="px-3 py-2">{row.first_name}</td>}
                            {row.last_name !== undefined && <td className="px-3 py-2">{row.last_name}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Lookback Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('audiences.lookbackDays', 'Periodo de Lookback')}
            </label>
            <select
              value={formData.lookback_days}
              onChange={(e) => setFormData({ ...formData, lookback_days: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value={7}>{t('audiences.days7', 'Ultimos 7 dias')}</option>
              <option value={14}>{t('audiences.days14', 'Ultimos 14 dias')}</option>
              <option value={30}>{t('audiences.days30', 'Ultimos 30 dias')}</option>
              <option value={60}>{t('audiences.days60', 'Ultimos 60 dias')}</option>
              <option value={90}>{t('audiences.days90', 'Ultimos 90 dias')}</option>
              <option value={180}>{t('audiences.days180', 'Ultimos 180 dias')}</option>
            </select>
          </div>

          {/* Dynamic / Static Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('audiences.updateMode', 'Modo de Atualização')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, is_dynamic: true }))}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  formData.is_dynamic
                    ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <RefreshCw className={`w-4 h-4 ${formData.is_dynamic ? 'text-primary-600' : 'text-gray-500'}`} />
                  <span className="font-medium text-gray-900 dark:text-white text-sm">
                    {t('audiences.dynamic', 'Dinâmica')}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('audiences.dynamicDesc', 'Atualiza automaticamente com novos dados')}
                </p>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, is_dynamic: false }))}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  !formData.is_dynamic
                    ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Filter className={`w-4 h-4 ${!formData.is_dynamic ? 'text-primary-600' : 'text-gray-500'}`} />
                  <span className="font-medium text-gray-900 dark:text-white text-sm">
                    {t('audiences.static', 'Estática')}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('audiences.staticDesc', 'Snapshot fixo, não atualiza automaticamente')}
                </p>
              </button>
            </div>
          </div>

          {/* Estimated Size */}
          {estimatedSize !== null && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900 dark:text-blue-100">
                  {t('audiences.estimatedSize', 'Tamanho Estimado:')} {estimatedSize.toLocaleString()} {t('audiences.users', 'usuarios')}
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="secondary"
              onClick={() => {
                setIsCreateModalOpen(false);
                resetForm();
              }}
            >
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button
              onClick={handleCreateAudience}
              isLoading={isSubmitting}
              leftIcon={<Zap className="w-4 h-4" />}
            >
              {t('audiences.create', 'Criar Audiencia')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* External Websites Modal */}
      <Modal
        isOpen={isExternalModalOpen}
        onClose={() => setIsExternalModalOpen(false)}
        title={t('audiences.externalWebsitesTitle', 'Sites Externos para Remarketing')}
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('audiences.externalDesc', 'Cadastre sites externos para rastrear visitantes e criar audiencias de remarketing. Apos cadastrar, instale o pixel de rastreamento no site.')}
          </p>

          {/* Add new external website form */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-3">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <Link className="w-4 h-4" />
              {t('audiences.addExternalSite', 'Cadastrar Site Externo')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                placeholder={t('audiences.externalName', 'Nome do site (ex: Loja Principal)')}
                value={externalForm.name}
                onChange={(e) => setExternalForm({ ...externalForm, name: e.target.value })}
              />
              <Input
                placeholder={t('audiences.externalUrl', 'URL (ex: https://meusite.com.br)')}
                value={externalForm.url}
                onChange={(e) => setExternalForm({ ...externalForm, url: e.target.value })}
              />
            </div>
            <Button
              size="sm"
              onClick={handleAddExternalWebsite}
              isLoading={isAddingExternal}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              {t('audiences.registerSite', 'Cadastrar')}
            </Button>
          </div>

          {/* List of external websites */}
          {externalWebsites.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              <Globe className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{t('audiences.noExternalSites', 'Nenhum site externo cadastrado')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {externalWebsites.map((site) => (
                <div key={site.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                        {site.name}
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 text-[10px] font-medium rounded">
                          <ExternalLink className="w-3 h-3" />
                          Externo
                        </span>
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{site.external_url}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteExternalWebsite(site.id)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Pixel snippet */}
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1">
                      <Code className="w-3 h-3" />
                      {t('audiences.pixelCode', 'Pixel de Rastreamento (instale no site):')}
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-gray-900 text-green-400 p-2 rounded font-mono truncate">
                        {site.pixel_snippet}
                      </code>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleCopyPixel(site.pixel_snippet, site.id)}
                        leftIcon={copiedPixelId === site.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      >
                        {copiedPixelId === site.id ? t('common.copied', 'Copiado') : t('common.copy', 'Copiar')}
                      </Button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="mt-2 flex gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <span>{site.views} {t('audiences.views', 'visitas')}</span>
                    <span>{site.conversions} {t('audiences.conversions', 'conversoes')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="secondary" onClick={() => setIsExternalModalOpen(false)}>
              {t('common.close', 'Fechar')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedAudience(null);
        }}
        title={t('audiences.deleteTitle', 'Excluir Audiencia')}
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            {t('audiences.deleteConfirm', 'Tem certeza que deseja excluir a audiencia "{{name}}"? Esta acao nao pode ser desfeita.').replace('{{name}}', selectedAudience?.name || '')}
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setSelectedAudience(null);
              }}
            >
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button
              variant="primary"
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteAudience}
              isLoading={isSubmitting}
            >
              {t('common.delete', 'Excluir')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AudiencesPage;
